/**
 * Geo-Tracking Consent Settings Component
 * Allows users to manage their location tracking and consent preferences
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { geoTracking, GeoConsentPreferences } from '@/services/geoTracking';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Loader2,
  Save,
  MapPin,
  Shield,
  Eye,
  EyeOff,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Navigation,
  FileText,
  Workflow,
  Home,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export function GeoTrackingConsent() {
  const { t } = useAppTranslation();
  const { user } = useAuth();

  // State for consent preferences
  const [preferences, setPreferences] = useState<GeoConsentPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Individual consent states
  const [addressGeocoding, setAddressGeocoding] = useState(false);
  const [siteUsageTracking, setSiteUsageTracking] = useState(false);
  const [specificLocation, setSpecificLocation] = useState(false);
  const [workflowTracking, setWorkflowTracking] = useState(false);
  const [formTracking, setFormTracking] = useState(false);
  const [allowOrgReporting, setAllowOrgReporting] = useState(false);
  const [dataRetentionDays, setDataRetentionDays] = useState(365);

  // Dialog states
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Location test state
  const [testingLocation, setTestingLocation] = useState(false);
  const [locationTest, setLocationTest] = useState<{
    latitude?: number;
    longitude?: number;
    accuracy?: number;
    error?: string;
  } | null>(null);

  useEffect(() => {
    loadPreferences();
  }, [user]);

  // Auto-save when consent preferences change
  useEffect(() => {
    if (!preferences || !user) return;

    // Check if any values have changed
    const changed =
      addressGeocoding !== preferences.address_geocoding_consent ||
      siteUsageTracking !== preferences.site_usage_tracking_consent ||
      specificLocation !== preferences.specific_location_consent ||
      workflowTracking !== preferences.workflow_tracking_consent ||
      formTracking !== preferences.form_tracking_consent ||
      allowOrgReporting !== preferences.allow_organizational_reporting ||
      dataRetentionDays !== preferences.data_retention_days;

    if (changed && !saving) {
      // Auto-save after a short delay to batch rapid changes
      const timer = setTimeout(() => {
        handleAutoSave();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [
    addressGeocoding,
    siteUsageTracking,
    specificLocation,
    workflowTracking,
    formTracking,
    allowOrgReporting,
    dataRetentionDays,
  ]);

  const loadPreferences = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const prefs = await geoTracking.getConsentPreferences(user.id, user.organization_id);

      if (prefs) {
        setPreferences(prefs);
        setAddressGeocoding(prefs.address_geocoding_consent);
        setSiteUsageTracking(prefs.site_usage_tracking_consent);
        setSpecificLocation(prefs.specific_location_consent);
        setWorkflowTracking(prefs.workflow_tracking_consent);
        setFormTracking(prefs.form_tracking_consent);
        setAllowOrgReporting(prefs.allow_organizational_reporting);
        setDataRetentionDays(prefs.data_retention_days);
      } else {
        // No preferences exist yet - show defaults
        setAddressGeocoding(false);
        setSiteUsageTracking(false);
        setSpecificLocation(false);
        setWorkflowTracking(false);
        setFormTracking(false);
        setAllowOrgReporting(false);
        setDataRetentionDays(365);
      }
    } catch (error) {
      console.error('Error loading geo consent preferences:', error);
      toast.error('Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSave = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const updatedPrefs = await geoTracking.updateConsentPreferences(user.id, {
        organization_id: user.organization_id,
        address_geocoding_consent: addressGeocoding,
        site_usage_tracking_consent: siteUsageTracking,
        specific_location_consent: specificLocation,
        workflow_tracking_consent: workflowTracking,
        form_tracking_consent: formTracking,
        allow_organizational_reporting: allowOrgReporting,
        data_retention_days: dataRetentionDays,
      });

      if (updatedPrefs) {
        setPreferences(updatedPrefs);
        toast.success('Consent Preferences Saved', {
          description: 'Your changes have been saved automatically.',
          duration: 2000,
        });

        // Clear service cache
        geoTracking.clearConsentCache();
      }
    } catch (error) {
      console.error('Error saving consent preferences:', error);
      toast.error('Save Failed', {
        description: 'Failed to save preferences. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHistory = async () => {
    if (!user) return;

    setDeleting(true);
    try {
      const success = await geoTracking.deleteTrackingHistory(user.id);
      if (success) {
        toast.success('History Deleted', {
          description: 'All your geo-tracking history has been permanently deleted.',
        });
        setShowDeleteDialog(false);
      } else {
        toast.error('Deletion Failed', {
          description: 'Could not delete tracking history. Please try again.',
        });
      }
    } catch (error) {
      console.error('Error deleting tracking history:', error);
      toast.error('Deletion Failed', {
        description: 'An error occurred while deleting your tracking history.',
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleTestLocation = async () => {
    setTestingLocation(true);
    setLocationTest(null);

    try {
      const position = await geoTracking.getCurrentLocation();
      if (position) {
        setLocationTest({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
        toast.success('Location Retrieved', {
          description: `Accuracy: ±${Math.round(position.coords.accuracy)}m`,
        });
      } else {
        setLocationTest({
          error: 'Could not get location. Please ensure location permissions are enabled.',
        });
        toast.error('Location Error', {
          description: 'Could not retrieve your location.',
        });
      }
    } catch (error) {
      setLocationTest({
        error: 'Location access denied or not available.',
      });
      toast.error('Location Error', {
        description: 'Failed to access location services.',
      });
    } finally {
      setTestingLocation(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="section-spacing">
      <div>
        <h2 className="text-heading-2 flex items-center gap-2">
          <MapPin className="h-6 w-6" />
          {t('settings.location_tracking_privacy', 'Location Tracking & Privacy')}
        </h2>
        <p className="text-secondary mt-2">
          {t('settings.control_how_your_location_data', 'Control how your location data is collected, used, and stored')}
        </p>
      </div>

      <Separator />

      {/* Privacy Overview Card */}
      <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            {t('settings.your_privacy_matters', 'Your Privacy Matters')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            <strong>{t('settings.you_are_in_control', 'You are in control.')}</strong> All location tracking is opt-in and you can
            change your preferences at any time.
          </p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>{t('settings.your_data_is_stored_securely', 'Your data is stored securely and encrypted')}</li>
            <li>{t('settings.you_can_view_and_delete', 'You can view and delete your tracking history at any time')}</li>
            <li>{t('settings.location_data_is_only_collected', 'Location data is only collected when you explicitly consent')}</li>
            <li>{t('settings.organizational_reporting_is_aggregated_and', 'Organizational reporting is aggregated and anonymized')}</li>
          </ul>
        </CardContent>
      </Card>

      {/* Address Geocoding */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-heading-4 flex items-center gap-2">
            <Home className="h-5 w-5" />
            {t('settings.address_geocoding', 'Address Geocoding')}
          </CardTitle>
          <CardDescription>
            {t('settings.convert_addresses_to_map_coordinates', 'Convert addresses to map coordinates for distance calculations and mapping')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <button
            type="button"
            onClick={() => setAddressGeocoding(!addressGeocoding)}
            className={`w-full flex items-start gap-4 p-5 rounded-xl transition-all duration-200 text-left ${
              addressGeocoding
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-2 border-green-300 dark:border-green-700 shadow-md'
                : 'bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm'
            }`}
          >
            <Checkbox
              id="address-geocoding"
              checked={addressGeocoding}
              onCheckedChange={setAddressGeocoding}
              className="h-6 w-6 mt-0.5 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="space-y-1 flex-1">
              <Label htmlFor="address-geocoding" className="text-base font-semibold cursor-pointer block">
                {t('settings.enable_address_geocoding', 'Enable Address Geocoding')}
              </Label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Allow the system to convert member addresses to latitude/longitude coordinates.
                This enables distance calculations, route planning, and map visualizations.
              </p>
            </div>
          </button>
        </CardContent>
      </Card>

      {/* Site Usage Tracking */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-heading-4 flex items-center gap-2">
            <Eye className="h-5 w-5" />
            {t('settings.site_usage_tracking', 'Site Usage Tracking')}
          </CardTitle>
          <CardDescription>
            {t('settings.track_which_pages_you_visit', 'Track which pages you visit and how you use the application')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <button
            type="button"
            onClick={() => setSiteUsageTracking(!siteUsageTracking)}
            className={`w-full flex items-start gap-4 p-5 rounded-xl transition-all duration-200 text-left ${
              siteUsageTracking
                ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border-2 border-blue-300 dark:border-blue-700 shadow-md'
                : 'bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm'
            }`}
          >
            <Checkbox
              id="site-usage"
              checked={siteUsageTracking}
              onCheckedChange={setSiteUsageTracking}
              className="h-6 w-6 mt-0.5 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="space-y-1 flex-1">
              <Label htmlFor="site-usage" className="text-base font-semibold cursor-pointer block">
                {t('settings.enable_site_usage_tracking', 'Enable Site Usage Tracking')}
              </Label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Track page visits and application usage patterns. This helps improve the user
                experience and identify areas for enhancement. Uses IP-based location (city/state
                level only).
              </p>
            </div>
          </button>
        </CardContent>
      </Card>

      {/* Specific Location (GPS) */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-heading-4 flex items-center gap-2">
            <Navigation className="h-5 w-5" />
            {t('settings.precise_location_tracking', 'Precise Location Tracking')}
          </CardTitle>
          <CardDescription>
            {t('settings.use_your_devices_gps_to', 'Use your device\'s GPS to track exact location during specific activities')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <button
            type="button"
            onClick={() => setSpecificLocation(!specificLocation)}
            className={`w-full flex items-start gap-4 p-5 rounded-xl transition-all duration-200 text-left ${
              specificLocation
                ? 'bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 border-2 border-purple-300 dark:border-purple-700 shadow-md'
                : 'bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm'
            }`}
          >
            <Checkbox
              id="specific-location"
              checked={specificLocation}
              onCheckedChange={setSpecificLocation}
              className="h-6 w-6 mt-0.5 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="space-y-1 flex-1">
              <Label htmlFor="specific-location" className="text-base font-semibold cursor-pointer block">
                {t('settings.enable_precise_location_tracking', 'Enable Precise Location Tracking')}
              </Label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Allow the app to access your device's GPS location during field visits, assessments,
                or service delivery. This provides exact coordinates for verification and reporting.
              </p>
            </div>
          </button>

          {specificLocation && (
            <div className="mt-4 p-4 rounded-lg bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{t('settings.test_location_access', 'Test Location Access')}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('settings.verify_that_location_services_are', 'Verify that location services are working correctly')}
                  </p>
                </div>
                <Button
                  onClick={handleTestLocation}
                  disabled={testingLocation}
                  variant="outline"
                  size="sm"
                >
                  {testingLocation ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('settings.getting_location', 'Getting Location...')}
                    </>
                  ) : (
                    <>
                      <Navigation className="mr-2 h-4 w-4" />
                      {t('settings.test_location', 'Test Location')}
                    </>
                  )}
                </Button>
              </div>

              {locationTest && (
                <div className="mt-3 p-3 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600">
                  {locationTest.error ? (
                    <div className="flex items-start gap-2 text-red-600 dark:text-red-400">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <div className="text-xs">
                        <p className="font-medium">{t('settings.location_error', 'Location Error')}</p>
                        <p>{locationTest.error}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 text-green-600 dark:text-green-400">
                      <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <div className="text-xs space-y-1">
                        <p className="font-medium">{t('settings.location_retrieved_successfully', 'Location Retrieved Successfully')}</p>
                        <p>Latitude: {locationTest.latitude?.toFixed(6)}</p>
                        <p>Longitude: {locationTest.longitude?.toFixed(6)}</p>
                        <p>Accuracy: ±{Math.round(locationTest.accuracy || 0)} meters</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Workflow Tracking */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-heading-4 flex items-center gap-2">
            <Workflow className="h-5 w-5" />
            {t('settings.workflow_activity_tracking', 'Workflow Activity Tracking')}
          </CardTitle>
          <CardDescription>
            {t('settings.track_location_during_workflow_step', 'Track location during workflow step completion and member assessments')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <button
            type="button"
            onClick={() => setWorkflowTracking(!workflowTracking)}
            className={`w-full flex items-start gap-4 p-5 rounded-xl transition-all duration-200 text-left ${
              workflowTracking
                ? 'bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30 border-2 border-orange-300 dark:border-orange-700 shadow-md'
                : 'bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm'
            }`}
          >
            <Checkbox
              id="workflow-tracking"
              checked={workflowTracking}
              onCheckedChange={setWorkflowTracking}
              className="h-6 w-6 mt-0.5 data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-600"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="space-y-1 flex-1">
              <Label htmlFor="workflow-tracking" className="text-base font-semibold cursor-pointer block">
                {t('settings.enable_workflow_tracking', 'Enable Workflow Tracking')}
              </Label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Record location when completing workflow steps such as enrollments, assessments, or
                service delivery. Helps verify field activity and improve service coordination.
              </p>
            </div>
          </button>
        </CardContent>
      </Card>

      {/* Form Tracking */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-heading-4 flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t('settings.form_submission_tracking', 'Form Submission Tracking')}
          </CardTitle>
          <CardDescription>
            {t('settings.track_location_when_submitting_forms', 'Track location when submitting forms and documents')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <button
            type="button"
            onClick={() => setFormTracking(!formTracking)}
            className={`w-full flex items-start gap-4 p-5 rounded-xl transition-all duration-200 text-left ${
              formTracking
                ? 'bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/30 dark:to-cyan-900/30 border-2 border-teal-300 dark:border-teal-700 shadow-md'
                : 'bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm'
            }`}
          >
            <Checkbox
              id="form-tracking"
              checked={formTracking}
              onCheckedChange={setFormTracking}
              className="h-6 w-6 mt-0.5 data-[state=checked]:bg-teal-600 data-[state=checked]:border-teal-600"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="space-y-1 flex-1">
              <Label htmlFor="form-tracking" className="text-base font-semibold cursor-pointer block">
                {t('settings.enable_form_tracking', 'Enable Form Tracking')}
              </Label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Record location when submitting forms, surveys, or documents. Useful for verifying
                on-site completion and compliance tracking.
              </p>
            </div>
          </button>
        </CardContent>
      </Card>

      {/* Organizational Reporting */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-heading-4 flex items-center gap-2">
            <Info className="h-5 w-5" />
            {t('settings.organizational_analytics', 'Organizational Analytics')}
          </CardTitle>
          <CardDescription>
            {t('settings.allow_your_organization_to_use', 'Allow your organization to use aggregated location data for reporting')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <button
            type="button"
            onClick={() => setAllowOrgReporting(!allowOrgReporting)}
            className={`w-full flex items-start gap-4 p-5 rounded-xl transition-all duration-200 text-left ${
              allowOrgReporting
                ? 'bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-900/30 dark:to-violet-900/30 border-2 border-indigo-300 dark:border-indigo-700 shadow-md'
                : 'bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm'
            }`}
          >
            <Checkbox
              id="org-reporting"
              checked={allowOrgReporting}
              onCheckedChange={setAllowOrgReporting}
              className="h-6 w-6 mt-0.5 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="space-y-1 flex-1">
              <Label htmlFor="org-reporting" className="text-base font-semibold cursor-pointer block">
                {t('settings.allow_organizational_reporting', 'Allow Organizational Reporting')}
              </Label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Let organization administrators view aggregated, anonymized location analytics.
                This includes service coverage maps, field activity reports, and usage patterns.
                Individual locations are never shown.
              </p>
            </div>
          </button>
        </CardContent>
      </Card>

      {/* Data Retention */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-heading-4">{t('settings.data_retention_period', 'Data Retention Period')}</CardTitle>
          <CardDescription>
            {t('settings.choose_how_long_to_keep', 'Choose how long to keep your location tracking data')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label htmlFor="retention" className="text-base font-semibold">{t('settings.retention_period', 'Retention Period')}</Label>
            <Select
              value={dataRetentionDays.toString()}
              onValueChange={(value) => setDataRetentionDays(parseInt(value))}
            >
              <SelectTrigger id="retention" className="w-full max-w-xs h-11 text-base">
                <SelectValue placeholder={t('settings.select_retention_period', 'Select retention period')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days (3 months)</SelectItem>
                <SelectItem value="180">180 days (6 months)</SelectItem>
                <SelectItem value="365">365 days (1 year)</SelectItem>
                <SelectItem value="730">730 days (2 years)</SelectItem>
                <SelectItem value="1825">1825 days (5 years)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Location data older than this period will be automatically deleted. Consent audit
              logs are retained permanently for compliance.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Privacy Controls */}
      <Card className="card-elevated border-red-200 dark:border-red-900">
        <CardHeader>
          <CardTitle className="text-heading-4 text-red-600 dark:text-red-400 flex items-center gap-2">
            <Trash2 className="h-5 w-5" />
            {t('settings.delete_tracking_history', 'Delete Tracking History')}
          </CardTitle>
          <CardDescription>
            {t('settings.permanently_delete_all_your_location', 'Permanently delete all your location tracking data')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This will permanently delete all geo-tracking events, location records, and usage data
            associated with your account. This action cannot be undone.
          </p>
          <Button variant="destructive" onClick={() => setShowDeleteDialog(true)} size="lg" className="shadow-md">
            <Trash2 className="mr-2 h-5 w-5" />
            {t('settings.delete_all_tracking_data', 'Delete All Tracking Data')}
          </Button>
        </CardContent>
      </Card>

      {/* Auto-save indicator */}
      {saving && (
        <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400 py-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>{t('settings.saving_preferences', 'Saving preferences...')}</span>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <AlertCircle className="h-5 w-5" />
              {t('settings.delete_all_tracking_data_1', 'Delete All Tracking Data?')}
            </DialogTitle>
            <DialogDescription>
              {t('settings.this_will_permanently_delete_all', 'This will permanently delete all your location tracking history, including:')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
              <li>{t('settings.all_geotracking_events_and_timestamps', 'All geo-tracking events and timestamps')}</li>
              <li>Location records (GPS coordinates, addresses)</li>
              <li>{t('settings.site_usage_history', 'Site usage history')}</li>
              <li>{t('settings.workflow_and_form_submission_locations', 'Workflow and form submission locations')}</li>
            </ul>
            <p className="mt-4 text-sm font-medium text-red-600 dark:text-red-400">
              This action cannot be undone. Your consent preferences and audit logs will be
              retained for compliance.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleting}>
              {t('settings.cancel', 'Cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDeleteHistory} disabled={deleting}>
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('settings.deleting', 'Deleting...')}
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('settings.delete_all_data', 'Delete All Data')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
